<template>
  <div class="html-editor">
    <div class="label-and-buttons">
      <label v-if="label" class="md-title">
        {{ label }}
        <help-tooltip v-if="errors && errors.length" type="error">
          <span v-for="error in errors" :key="error.message">
            {{ error.message }}
            <br>
          </span>
        </help-tooltip>
      </label>
      <div v-if="subtitle" class="md-subtitle">{{ subtitle }}</div>
      <div v-if="!readonly">
        <md-button
          class="toggle-button md-dense"
          :disabled="!editor || !editor.can().chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor && editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <md-icon>
            format_bold
          </md-icon>
        </md-button>
        <md-button
          class="toggle-button md-dense"
          :disabled="!editor || !editor.can().chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor && editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <md-icon>
            format_italic
          </md-icon>
        </md-button>
        <md-button
          class="toggle-button md-dense"
          :disabled="!editor || !editor.can().chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor && editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <md-icon>
            format_underlined
          </md-icon>
        </md-button>
        <md-button
          class="toggle-button md-dense"
          :disabled="!editor || !editor.can().chain().focus().toggleBulletList().run()"
          :class="{ 'is-active': editor && editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <md-icon>
            format_list_bulleted
          </md-icon>
        </md-button>
        <md-button
          class="toggle-button md-dense"
          :disabled="!editor || !editor.can().chain().focus().toggleOrderedList().run()"
          :class="{ 'is-active': editor && editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <md-icon>
            format_list_numbered
          </md-icon>
        </md-button>
      </div>
    </div>
    <md-content
      class="md-scrollbar"
      :class="{ 'show-border': showBorder }"
      :style="height ? { flex: 'none', overflow: 'hidden' } : null"
    >
      <editor-content
        class="editor"
        :editor="editor"
      />
    </md-content>
    <span
      v-if="maxChars"
      class="max-char-span"
      :class="{ 'max-char-span-error': maxChars && rawTextLength > maxChars }"
    >
      {{ rawTextLength }} / {{ maxChars }}
    </span>
  </div>
</template>

<script>
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Indent } from './indent-extension';
import sanitizeInput from './sanitize-input';

export default {
  name: 'tiptap',
  components: {
    EditorContent,
    HelpTooltip,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    subtitle: {
      type: String,
      default: null,
    },
    contents: {
      type: String,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    showBorder: {
      type: Boolean,
      default: true,
    },
    reload: {
      type: Boolean,
      default: false
    },
    maxChars: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default: null,
    },
    preventEmojis: {
      type: Boolean,
      default: false
    },
    height: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      editor: null,
      rawTextLength: 0,
      lastValidHtml: null,
    }
  },
  watch: {
    contents(newValue) {
      if (this.readonly || this.reload) {
        // For prompt preview, reload contents when they change
        // If editing, don't
        this.editor.commands.setContent(newValue);
      }
    }
  },
  mounted() {
    const outerThis = this;
    const heightPx = this.height
      ? (typeof this.height === 'number' ? this.height + 'px' : this.height)
      : null;
    this.editor = new Editor({
      content: this.contents,
      editable: !this.readonly,
      editorProps: heightPx ? {
        attributes: {
          style: `min-height: ${heightPx}; resize: vertical; overflow: hidden; box-sizing: border-box;`,
        },
      } : {},
      extensions: [
        StarterKit,
        Underline,
        Indent,
        Placeholder.configure({
          // Use a placeholder:
          placeholder: this.placeholder
        }),
      ],
      onUpdate({editor}) {
        // Enforce character limit: revert to last valid content when exceeded.
        if (outerThis.maxChars && editor.getText().length > outerThis.maxChars) {
          editor.commands.setContent(outerThis.lastValidHtml, false);
          return;
        }

        // Some ChatGPT supplied code to replace emojis with spaces when they're
        // typed or pasted
        if (!outerThis.preventEmojis) {
          return;
        }
        const transaction = editor.state.tr;
        let hasChanges = false;

        editor.state.doc.descendants((node, pos) => {
          if (node.isText) {
            let sanitizedText = sanitizeInput(node.text);
            if (!sanitizedText || sanitizedText.length === 0) {
              sanitizedText = ' '; // tiptap won't allow empty text nodes
            }
            if (sanitizedText !== node.text) {
              transaction.replaceWith(pos, pos + node.nodeSize, editor.schema.text(sanitizedText));
              hasChanges = true;
            }
          }
        });

        if (hasChanges) {
          editor.view.dispatch(transaction);
        }
      }
    })
    this.lastValidHtml = this.contents || '';
    this.editor.on('update', ({ editor }) => {
      this.lastValidHtml = editor.getHTML();
      this.emitUpdate(editor.getHTML());
      this.autoResize();
    });
    this.updateRawTextLength(this.editor.getText());
    this.$nextTick(() => requestAnimationFrame(this.autoResize));
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    autoResize() {
      if (!this.height || !this.editor) return;
      const el = this.editor.view.dom;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    emitUpdate(contents) {
      const text = this.editor.getText();
      this.updateRawTextLength(text);
      this.$emit('update', {
        markup: contents,
        content: text
      });
    },
    updateRawTextLength(rawText) {
      const newLength = rawText && rawText.length ? rawText.length : 0;
      this.rawTextLength = newLength;
      const disabled = this.maxChars && newLength > this.maxChars;
      this.$emit('disabled', disabled);
    }
  }
}
</script>

<style scoped>
.html-editor {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.label-and-buttons {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 16px;
  font-weight: 500;
}

.md-content {
  overflow-x: hidden;
  flex: 1;
  box-sizing: border-box;
}

.editor {
  min-height: 100%;
}

.editor >>> .tiptap.ProseMirror {
  margin: 0;
  background-color: white;
  outline: none;
  padding: 6px 10px;
  border: none;
  min-height: 100%;
}

.show-border {
  border-radius: 5px;
  border: 1px solid #c4c4c4;
}

.editor >>> p {
  margin-block-start: 0;
  margin-block-end: 0;
}

.editor >>> ul,
.editor >>> ol {
  padding-left: 1.5em;
  margin-block-start: 0;
  margin-block-end: 0;
}

.editor >>> ol {
  list-style-type: decimal;
}

.editor >>> ol ol {
  list-style-type: lower-alpha;
}

.editor >>> ol ol ol {
  list-style-type: lower-roman;
}

.editor >>> ul {
  list-style-type: disc;
}

.toggle-button {
  min-width: 32px;
  width: 32px;
  margin: 0 6px 6px 0;
}

.toggle-button:first-of-type {
  margin-left: 6px;
}

.toggle-button.is-active {
  background-color: #E0E0E0;
}

.max-char-span {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  align-self: flex-end;
}

.max-char-span-error {
  color: #f92c2d;
}

.md-subtitle {
  margin-bottom: 6px;
}


</style>

<style>
.ProseMirror:focus {
  outline: none;
}
.tiptap p.is-editor-empty:first-child::before {
  /* color: #adb5bd; */
  color: gray;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
</style>